<script setup>
    import { format } from 'v-money3';
    import { storeToRefs } from 'pinia';
    import { moneyOptions } from '@/core/helpers';
    import { useQuoteStore } from '@/stores/quote';
    import Tooltip from 'bootstrap/js/dist/tooltip';
    import { useStepperStore } from '@/stores/stepper';
    import { useCheckoutStore } from '@/stores/checkout';
    import BaseButton from '@/components/atoms/BaseButton.vue';
    import BaseMessage from '@/components/atoms/BaseMessage.vue';
    import { QuoteService } from '@/core/services/quoteService';
    import { FormDataService } from '@/core/services/formDataService';
    import { AnalyticsService } from '@/core/services/analyticsService';
    import ApplePayIcon from '@/components/atoms/icons/Checkout/ApplePay.vue';
    import GooglePayIcon from '@/components/atoms/icons/Checkout/GooglePay.vue';
    import { ref, computed, inject, nextTick, onBeforeUnmount, watch } from 'vue';
    import { resolveCheckoutDiscountTooltip, resolveCheckoutDiscountMandatory } from '@/core/checkoutDiscountTooltip';
    import { PaymentMethods, vivaPaymentMethods, paymentOptions } from '@/enums/payments';
    import { trackGTMEvent } from '@/core/helpers';

    const quoteStore = useQuoteStore();
    const quoteService = QuoteService();
    const stepperStore = useStepperStore();
    const checkoutStore = useCheckoutStore();
    const formDataService = FormDataService();
    const analyticsService = AnalyticsService();

    const isLessXl = inject('isLessXl', computed(() => false));

    const quote = quoteService.get();
    const { nextStep, submitPayment } = stepperStore;
    const { isLastStep } = storeToRefs(stepperStore);
    const {
        submitHandler, 
        paymentMethod,
        displayedPrice,
        discountedPrice,
        checkoutLoading, 
        discountApplied,
        selectedProduct,
        discountPercentage,
    } = storeToRefs(checkoutStore);

    const discountCode = ref('');
    const showdiscountFields = ref(false);
    const applyDiscountFailed = ref(false);
    const applyDiscountSuccess = ref(false);
    const applyDiscountLoading = ref(false);
    const discountInfoTooltipRef = ref(null);
    const formData = ref(formDataService.get(quote.vehicleType) ?? {});

    let discountInfoTooltipInstance = null;
    const discountInfoTooltip = computed(() => resolveCheckoutDiscountTooltip(selectedProduct.value));
    const isDiscountMandatory = computed(() => resolveCheckoutDiscountMandatory(selectedProduct.value));

    const showDiscountSection = computed(
        () => checkoutStore.getSelectedProductDiscounts() && !isLessXl.value && !isDiscountMandatory.value,
    );

    const displayedSuffix = computed(() => 'σύνολο');

    const finalPrice = computed(() =>
        displayedPrice.value
    );

    const discountedFinalPrice = computed(() =>
        discountedPrice.value
    );

    const durationMonths = computed(() => {
        const parsed = Number(formData.value?.duration);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 12;
    });

    const monthlyFinalPrice = computed(() => finalPrice.value / durationMonths.value);
    const monthlyDiscountedFinalPrice = computed(() => discountedFinalPrice.value / durationMonths.value);

    const paymentMethodLabels = {
        [PaymentMethods.IRIS]: 'μέσω Iris',
        [PaymentMethods.CARD]: 'μέσω Κάρτας',
    };

    const isVivaPayment = computed(() => vivaPaymentMethods.includes(paymentMethod.value));
    const isDarkPayment = computed(() => (
        [PaymentMethods.APPLE_PAY, PaymentMethods.GOOGLE_PAY].includes(paymentMethod.value)
    ));

    const paymentButtonLabel = computed(() => {
        const paymentText = 'Πληρωμή';

        if (!isLastStep.value) {
            return 'Επόμενο';
        }

        if (!isVivaPayment.value) {
            return `Μετάβαση στην ${paymentText.toLowerCase()}`;
        }

        if (isDarkPayment.value) {
            return paymentText;
        }

        const methodLabel = paymentMethodLabels[paymentMethod.value] ?? '';
        return `${paymentText} ${methodLabel}`;
    });

    const paymentButtonIcon = computed(() => {
        if (!isLastStep.value || !isDarkPayment.value) {
            return null;
        }

        if (paymentMethod.value === PaymentMethods.GOOGLE_PAY) {
            return GooglePayIcon;
        }

        return ApplePayIcon;
    });

    const paymentButtonClassList = computed(() => {
        const baseClasses = 'btn btn-primary btn--im flex-shrink-0 w-100 py-3 fw-bold';
        if (!isLastStep.value) {
            return baseClasses;
        }

        const variantClass = isDarkPayment.value
            ? 'checkout-payment-btn--dark'
            : 'checkout-payment-btn--primary';
        return [baseClasses, variantClass];
    });

    const submitForm = async () => {
        if (await submitHandler.value()) {
            nextStep()
        }
    }

    const handleSubmit = async () => {
        if (isLastStep.value) {
            try {
                await analyticsService.savePaymentMethodSelection(
                    quoteStore.quote?.trackingData?.preBuyID,
                    paymentMethod.value,
                );
            } catch (error) {}

            if (paymentMethod.value === PaymentMethods.BANK_TRANSFER) {
                checkoutStore.setBankTransferPaymentActive(true);
                return;
            }

            const selectedProduct = checkoutStore.getSelectedProduct()
            const offerCode = checkoutStore.getOfferCode();
            const vehicleType = checkoutStore.getVehicleType();
            const roadAssistanceSelected = checkoutStore.getRoadAssistanceSelected();

            trackGTMEvent({
	            "event": "complete_payment_success",
	            "offer_code": offerCode,
	            "category": vehicleType,
	            "checkout_type": selectedProduct.easyBuy ? 'easy_buy' : 'non_easy_buy',
	            "complete_value": selectedProduct.price.toFixed(2),
	            "odikiExists": roadAssistanceSelected,
                "payment_type": paymentMethod.value
            })

            const selectedOption = paymentOptions.find(opt => opt.value.method === paymentMethod.value);
            submitPayment(selectedOption?.value?.id);
            return;
        }

        submitForm();
    };

    const applyDiscount = async () => {
        applyDiscountLoading.value = true;
        applyDiscountFailed.value = false;
        applyDiscountSuccess.value = false;

        try {
            const isValid = await checkoutStore.applyDiscountCode(discountCode.value);
            applyDiscountSuccess.value = isValid;
            applyDiscountFailed.value = !isValid;
        } catch (error) {
            applyDiscountFailed.value = true;
            applyDiscountSuccess.value = false;
        } finally {
            applyDiscountLoading.value = false;
        }
    };

    const setupDiscountInfoTooltip = () => {
        if (discountInfoTooltipInstance) {
            discountInfoTooltipInstance.dispose();
            discountInfoTooltipInstance = null;
        }

        if (!discountInfoTooltip.value || !discountInfoTooltipRef.value) {
            return;
        }

        discountInfoTooltipInstance = new Tooltip(discountInfoTooltipRef.value, {
            html: true,
            trigger: 'click',
            customClass: 'checkout-discount-tooltip',
        });
    };

    watch([discountInfoTooltip, showDiscountSection], async () => {
        await nextTick();
        setupDiscountInfoTooltip();
    }, { immediate: true });

    onBeforeUnmount(() => {
        if (!discountInfoTooltipInstance) {
            return;
        }

        discountInfoTooltipInstance.dispose();
        discountInfoTooltipInstance = null;
    });
</script>

<template>
    <div class="checkout-actions rounded-top-4 bg-white shadow-lg">
        <!-- Discounts -->
        <div v-if="showDiscountSection" class="checkout-discounts d-flex justify-content-center align-items-center py-2">
            <div class="checkout-discounts__row">
                <div class="checkout-discounts__title">
                    <span
                        v-if="discountInfoTooltip && !isDiscountMandatory"
                        ref="discountInfoTooltipRef"
                        class="d-inline-flex align-items-center cursor-pointer"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        :data-bs-title="discountInfoTooltip"
                    >
                        <vue-feather type="info" size="16" stroke-width="2"></vue-feather>
                    </span>
                    <span class="coupon-icon" aria-hidden="true">🎟️</span>
                    <span class="fs-5 me-10">Έχεις κωδικό έκπτωσης;</span>
                </div>

                <transition name="discount-toggle" mode="out-in">
                    <div v-if="!showdiscountFields" key="discount-switch" class="form-check form-switch m-0 custom-switch">
                        <input
                            class="form-check-input custom-toggle"
                            type="checkbox"
                            role="switch"
                            id="monthlyInstallmentsCheckbox"
                            v-model="showdiscountFields"
                        />
                    </div>

                    <div v-else key="discount-controls" class="checkout-discounts__controls">
                        <div
                            class="checkout-discounts__pill"
                            :class="{
                                'is-invalid': applyDiscountFailed && !applyDiscountSuccess,
                                'is-success': applyDiscountSuccess
                            }"
                        >
                            <input
                                class="form-control checkout-discounts__input"
                                type="text"
                                placeholder="Εισαγωγή κωδικού"
                                v-model="discountCode"
                                @input="
                                    applyDiscountFailed = false;
                                    applyDiscountSuccess = false;
                                "
                            />

                            <BaseButton
                                @click="applyDiscount"
                                :loading="applyDiscountLoading"
                                :disabled="applyDiscountLoading"
                                classList="btn rounded-3 checkout-discounts__apply">
                                <div class="btn-content d-flex align-items-center">
                                    <div class="btn-back-text ps-1">Εφαρμογή</div>
                                </div>
                            </BaseButton>
                        </div>

                        <button
                            v-if="showdiscountFields"
                            type="button"
                            class="pt-2 border-0 bg-transparent cursor-pointer"
                            :disabled="applyDiscountLoading"
                            @click="
                                showdiscountFields = false;
                                applyDiscountSuccess = false;
                                applyDiscountFailed = false;
                            "
                        >
                            <vue-feather type="x" size="20" stroke-width="2"></vue-feather>
                        </button>
                    </div>
                </transition>

                <BaseMessage
                    v-if="applyDiscountFailed"
                    message="Λανθασμένος κωδικός"
                    messageClass="mw-300"
                    variant="danger"
                />
            </div>
        </div>

        <div class="container-form container">
            <div class="row align-items-center mb-4 mt-2">
                <!-- Summary -->
                <div class="col-12 col-md-6">
                    <div class="d-flex flex-column justify-content-center align-items-center fw-bold mb-1 mb-lg-0" v-if="!discountApplied || isDiscountMandatory">
                        <div class="d-flex justify-content-center align-items-center gap-2">
                            <span class="fs-2 text-dark"> {{ format(finalPrice, moneyOptions) }} </span>
                            <span class="d-flex align-items-end text-muted mt-2 fs-4">{{ displayedSuffix }}</span>
                        </div>
                        <div class="d-flex align-items-end text-muted fs-5">{{ format(monthlyFinalPrice, moneyOptions) }}/μήνα</div>
                    </div>

                    <div class="d-flex flex-column justify-content-center align-items-center gap-1 fw-bold mb-1 mb-lg-0" v-if="discountApplied && !isDiscountMandatory">
                        <div class="d-flex flex-row justify-content-center align-items-center gap-2 fw-bold">
                            <span class="text-n600 fs-4 text-decoration-line-through fw-lighter"> {{ format(finalPrice, moneyOptions) }}</span>
                            <span class="text-n600 fs-4 fw-lighter">(αρχική τιμή)</span>
                        </div>
                        <div class="d-flex flex-column justify-content-center align-items-center">
                            <div class="d-flex justify-content-center align-items-center">
                                <span class="fs-2 text-dark"> {{ format(discountedFinalPrice, moneyOptions) }}</span>
                                <span class="d-flex align-items-end text-muted ms-1 mt-2 fs-4">{{ displayedSuffix }}</span>
                                <span v-if="discountPercentage" class="checkout-discount-badge mt-2 ms-2">
                                    -5%
                                </span>
                            </div>
                            <div class="d-flex align-items-end text-muted fs-5">{{ format(monthlyDiscountedFinalPrice, moneyOptions) }}/μήνα</div>
                        </div>
                    </div>

                    <!-- Toggle control -->
                    <!-- <div v-if="isLastStep" class="text-secondary small text-center mb-1 mb-lg-0">
                        Θα μεταφερθείς αυτόματα σε ασφαλές περιβάλλον πληρωμής.
                    </div> -->
                </div>

                <!-- Primary action -->
                <div class="col-12 col-md-4">
                    <BaseButton
                        @click.prevent="handleSubmit"
                        :class="{ 'btn--is-loading': checkoutLoading }"
                        :disabled="checkoutLoading || applyDiscountLoading"
                        :classList="paymentButtonClassList">
                        <span class="btn-content btn-label d-flex align-items-center justify-content-center gap-2">
                            <span>{{ paymentButtonLabel }}</span>
                            <component
                                v-if="paymentButtonIcon"
                                :is="paymentButtonIcon"
                                :class="[
                                    'checkout-payment-btn__icon',
                                    {
                                        'checkout-payment-btn__icon--apple': paymentMethod === PaymentMethods.APPLE_PAY,
                                        'checkout-payment-btn__icon--gpay': paymentMethod === PaymentMethods.GOOGLE_PAY,
                                    },
                                ]"
                            />
                            <vue-feather v-if="!isLastStep" size="18" stroke-width="2" type="chevron-right" />
                        </span>
                    </BaseButton>
                </div>
            </div>
        </div>
    </div>
</template>
